import React from 'react';
import { Box } from '@mui/material';
import { useTranslate } from 'react-admin';
import MaterialSymbol from '../../common/MaterialSymbol';

interface StarRatingProps {
    /** 0–5; Dezimalwerte (Durchschnitt) werden auf ganze Sterne gerundet angezeigt */
    value: number;
    size?: number;
    /** Wenn gesetzt, ist die Wertung bedienbar (Radiogruppe) */
    onChange?: (value: number) => void;
    testId?: string;
}

const STARS = [1, 2, 3, 4, 5];

/**
 * Sterne-Anzeige bzw. -Eingabe. Gefüllt = Gold (Token --oa-gold), leer = Grau; kein Verlauf,
 * kein Kursiv. Die Eingabe ist eine Radiogruppe (Tastatur: Pfeiltasten wie bei
 * nativen Radios über je einen Button pro Stern, Beschriftung „n Sterne“).
 */
const StarRating: React.FC<StarRatingProps> = ({ value, size = 20, onChange, testId }) => {
    const translate = useTranslate();
    const rounded = Math.round(value);

    if (!onChange) {
        return (
            <Box
                role="img"
                aria-label={translate('marketplaceReviews.stars', { count: Number.isInteger(value) ? value : value.toFixed(1) })}
                data-testid={testId}
                sx={{ display: 'inline-flex', gap: 0.25 }}
            >
                {STARS.map((n) => (
                    <Box key={n} sx={{ display: 'flex', color: n <= rounded ? 'var(--oa-gold)' : 'var(--color-neutral-300)' }}>
                        <MaterialSymbol name="star" size={size} filled={n <= rounded} />
                    </Box>
                ))}
            </Box>
        );
    }

    return (
        <Box role="radiogroup" aria-label={translate('marketplaceReviews.ratingLabel')} data-testid={testId} sx={{ display: 'inline-flex', gap: 0.5 }}>
            {STARS.map((n) => (
                <Box
                    key={n}
                    component="button"
                    type="button"
                    role="radio"
                    aria-checked={n === value}
                    aria-label={translate('marketplaceReviews.starInput', { count: n })}
                    onClick={() => onChange(n)}
                    sx={{
                        display: 'flex',
                        p: 0.5,
                        border: 0,
                        borderRadius: 'var(--oa-radius-sm, 4px)',
                        bgcolor: 'transparent',
                        cursor: 'pointer',
                        color: n <= value ? 'var(--oa-gold)' : 'var(--color-neutral-300)',
                        '&:focus-visible': { outline: '2px solid var(--oa-primary)', outlineOffset: 2 },
                    }}
                >
                    <MaterialSymbol name="star" size={size + 4} filled={n <= value} />
                </Box>
            ))}
        </Box>
    );
};

export default StarRating;
